import React from 'react';
import {
  Plus, X, BarChart3, LineChart, PieChart, AreaChart, TrendingDown,
  Gauge, Grid3x3, CircleDot, Activity, Target,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AGGREGATIONS, displayLabel } from '../../analytics-shared/engine/reportEngine';
import { uid } from '../api/reportsApi';
import ChartRenderer from '../../analytics-shared/components/ChartRenderer';

const CHART_TYPES = [
  { value: 'bar', label: 'Bar', icon: BarChart3 },
  { value: 'line', label: 'Line', icon: LineChart },
  { value: 'area', label: 'Area', icon: AreaChart },
  { value: 'combo', label: 'Combo', icon: Activity },
  { value: 'pie', label: 'Pie', icon: PieChart },
  { value: 'donut', label: 'Donut', icon: CircleDot },
  { value: 'funnel', label: 'Funnel', icon: TrendingDown },
  { value: 'kpi', label: 'KPI', icon: Gauge },
  { value: 'heatmap', label: 'Heatmap', icon: Grid3x3 },
];

const CATEGORICAL_TYPES = ['select', 'user', 'text', 'date', 'tags', 'multiselect', 'lookup', 'checkbox'];
const NUMERIC_TYPES = ['number', 'currency', 'percent'];
// Chart types that support a breakdown (second grouping) series.
const BREAKDOWN_TYPES = ['bar', 'heatmap'];
// Chart types that support a goal line / target.
const GOAL_TYPES = ['bar', 'line', 'area', 'combo', 'kpi'];

const Select = ({ value, onChange, children, disabled }) => (
  <select value={value} onChange={onChange} disabled={disabled}
    className="w-full h-7 text-xs border border-slate-200 rounded px-1 bg-white disabled:opacity-50">
    {children}
  </select>
);

const Label = ({ children }) => (
  <label className="text-[10px] text-slate-400 uppercase font-medium">{children}</label>
);

const ChartBuilder = ({ object, config, fieldMap, result, onChange }) => {
  // Full chartData entries (data + seriesKeys + goalValue) from the last
  // engine run, keyed by chart id.
  const chartEntryById = React.useMemo(() => {
    const map = {};
    (result?.chartData || []).forEach((c) => { if (c?.id) map[c.id] = c; });
    return map;
  }, [result?.chartData]);
  const charts = config.charts || [];

  // Multi-object aware: prefer the field map (namespaced related +
  // computed fields), fall back to single-object metadata.
  const allFields = React.useMemo(() => {
    const fm = fieldMap && Object.keys(fieldMap).length ? Object.values(fieldMap) : (object?.fields || []);
    return [...fm].sort((a, b) => (displayLabel(a) || '').localeCompare(displayLabel(b) || ''));
  }, [fieldMap, object]);
  const categoricalFields = allFields.filter((f) => CATEGORICAL_TYPES.includes(f.type));
  const numericFields = allFields.filter((f) => NUMERIC_TYPES.includes(f.type));

  const addChart = () => {
    const groupField = (config.groupBy[0]) || (categoricalFields[0] && categoricalFields[0].key);
    const valueField = numericFields[0] ? numericFields[0].key : '';
    onChange([...charts, {
      id: uid('ch'), type: 'bar', groupField, valueField,
      agg: valueField ? 'SUM' : 'COUNT', title: 'New Chart',
    }]);
  };
  const updateChart = (id, patch) => onChange(charts.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const removeChart = (id) => onChange(charts.filter((c) => c.id !== id));

  const fieldOptions = (fields) => fields.map((f) => (
    <option key={f.key} value={f.key}>{displayLabel(f) || f.label}</option>
  ));

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-3 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          <BarChart3 className="h-4 w-4 text-indigo-600" /> Charts
          {charts.length > 0 && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 rounded-full">{charts.length}</span>}
        </h3>
        <Button size="sm" onClick={addChart} className="h-7 bg-indigo-600 hover:bg-indigo-700">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Chart
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {charts.length === 0 && (
          <div className="text-center py-10">
            <BarChart3 className="h-9 w-9 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500 font-medium">No charts yet</p>
            <p className="text-xs text-slate-400 mt-1">Charts are generated live from your table data.</p>
          </div>
        )}

        {charts.map((chart) => {
          const valueLabel = chart.agg === 'COUNT'
            ? 'Count'
            : `${chart.agg} ${displayLabel(fieldMap[chart.valueField]) || ''}`;
          const entry = chartEntryById[chart.id] || {};
          const supportsBreakdown = BREAKDOWN_TYPES.includes(chart.type);
          const supportsGoal = GOAL_TYPES.includes(chart.type);
          const goal = chart.goal || { mode: 'none' };
          const rules = chart.colorRules || [];
          const ruleValue = (color) => rules.find((r) => r.color === color)?.value ?? '';
          const setKpiRules = (greenV, amberV) => {
            const out = [];
            const g = Number(greenV);
            const a = Number(amberV);
            if (!Number.isNaN(g) && greenV !== '') out.push({ operator: 'gte', value: g, color: '#059669' });
            if (!Number.isNaN(a) && amberV !== '') {
              out.push({ operator: 'gte', value: a, color: '#d97706' });
              out.push({ operator: 'lt', value: a, color: '#dc2626' });
            }
            updateChart(chart.id, { colorRules: out.length ? out : null });
          };

          return (
            <div key={chart.id} className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-1.5 px-2.5 py-2 bg-slate-50 border-b border-slate-200">
                <Input value={chart.title} onChange={(e) => updateChart(chart.id, { title: e.target.value })}
                  className="h-7 text-xs font-medium border-0 bg-transparent px-1 focus-visible:ring-1" />
                <button onClick={() => removeChart(chart.id)} className="text-slate-300 hover:text-red-500">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-5 gap-1 p-2 border-b border-slate-100">
                {CHART_TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = chart.type === t.value;
                  return (
                    <button key={t.value} onClick={() => updateChart(chart.id, { type: t.value })}
                      className={`flex flex-col items-center gap-0.5 py-1.5 rounded-lg text-[10px] transition-colors ${
                        active ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-100'
                      }`}>
                      <Icon className="h-3.5 w-3.5" />
                      {t.label}
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-3 gap-1.5 p-2 border-b border-slate-100">
                <div>
                  <Label>Group (X)</Label>
                  <Select value={chart.groupField || ''} onChange={(e) => updateChart(chart.id, { groupField: e.target.value })}>
                    {fieldOptions(categoricalFields)}
                  </Select>
                </div>
                <div>
                  <Label>Measure (Y)</Label>
                  <Select value={chart.valueField || ''} onChange={(e) => updateChart(chart.id, { valueField: e.target.value })}>
                    <option value="">Record Count</option>
                    {fieldOptions(numericFields)}
                  </Select>
                </div>
                <div>
                  <Label>Aggregate</Label>
                  <Select value={chart.agg} onChange={(e) => updateChart(chart.id, { agg: e.target.value })} disabled={!chart.valueField}>
                    {AGGREGATIONS.filter((a) => chart.valueField || a.value === 'COUNT').map((a) => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Advanced settings — breakdown / stacking / combo / goal / KPI colors */}
              <div className="p-2 border-b border-slate-100 space-y-2">
                {supportsBreakdown && (
                  <div className="grid grid-cols-2 gap-1.5 items-end">
                    <div>
                      <Label>Break down by</Label>
                      <Select value={chart.breakdownField || ''} onChange={(e) => updateChart(chart.id, { breakdownField: e.target.value || null })}>
                        <option value="">None</option>
                        {fieldOptions(categoricalFields.filter((f) => f.key !== chart.groupField))}
                      </Select>
                    </div>
                    {chart.type === 'bar' && chart.breakdownField && (
                      <div className="flex rounded-md overflow-hidden border border-slate-200 h-7">
                        {[['grouped', 'Grouped'], ['stacked', 'Stacked'], ['stack100', '100%']].map(([mode, label]) => {
                          const active = mode === 'stack100' ? chart.stack100 : mode === 'stacked' ? (chart.stacked && !chart.stack100) : (!chart.stacked && !chart.stack100);
                          return (
                            <button key={mode} type="button"
                              onClick={() => updateChart(chart.id, {
                                stacked: mode !== 'grouped',
                                stack100: mode === 'stack100',
                              })}
                              className={`flex-1 text-[10px] font-semibold ${active ? 'bg-indigo-600 text-white' : 'bg-white text-slate-500'}`}>
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {chart.type === 'combo' && (
                  <div className="grid grid-cols-2 gap-1.5">
                    <div>
                      <Label>Line measure (right axis)</Label>
                      <Select value={chart.valueField2 || ''} onChange={(e) => updateChart(chart.id, { valueField2: e.target.value || null })}>
                        <option value="">Record Count</option>
                        {fieldOptions(numericFields)}
                      </Select>
                    </div>
                    <div>
                      <Label>Line aggregate</Label>
                      <Select value={chart.agg2 || 'COUNT'} onChange={(e) => updateChart(chart.id, { agg2: e.target.value })} disabled={!chart.valueField2}>
                        {AGGREGATIONS.filter((a) => chart.valueField2 || a.value === 'COUNT').map((a) => (
                          <option key={a.value} value={a.value}>{a.label}</option>
                        ))}
                      </Select>
                    </div>
                  </div>
                )}

                {supportsGoal && (
                  <div className="grid grid-cols-3 gap-1.5 items-end">
                    <div>
                      <Label><Target className="h-2.5 w-2.5 inline mr-0.5 -mt-px" />Goal line</Label>
                      <Select value={goal.mode || 'none'} onChange={(e) => {
                        const mode = e.target.value;
                        updateChart(chart.id, { goal: mode === 'none' ? null : { ...goal, mode } });
                      }}>
                        <option value="none">None</option>
                        <option value="number">Fixed number</option>
                        <option value="field">From a field</option>
                      </Select>
                    </div>
                    {goal.mode === 'number' && (
                      <Input type="number" placeholder="Target" value={goal.value ?? ''}
                        onChange={(e) => updateChart(chart.id, { goal: { ...goal, value: e.target.value } })}
                        className="h-7 text-xs" />
                    )}
                    {goal.mode === 'field' && (
                      <>
                        <Select value={goal.field || ''} onChange={(e) => updateChart(chart.id, { goal: { ...goal, field: e.target.value } })}>
                          <option value="">Choose field…</option>
                          {fieldOptions(numericFields)}
                        </Select>
                        <Select value={goal.agg || 'SUM'} onChange={(e) => updateChart(chart.id, { goal: { ...goal, agg: e.target.value } })}>
                          {AGGREGATIONS.filter((a) => a.value !== 'COUNT').map((a) => (
                            <option key={a.value} value={a.value}>{a.label}</option>
                          ))}
                        </Select>
                      </>
                    )}
                  </div>
                )}

                {chart.type === 'kpi' && (
                  <div className="grid grid-cols-2 gap-1.5">
                    <div>
                      <Label><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1" />Green if ≥</Label>
                      <Input type="number" value={ruleValue('#059669')}
                        onChange={(e) => setKpiRules(e.target.value, ruleValue('#d97706'))}
                        className="h-7 text-xs" placeholder="e.g. 100" />
                    </div>
                    <div>
                      <Label><span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1" />Amber if ≥ (else red)</Label>
                      <Input type="number" value={ruleValue('#d97706')}
                        onChange={(e) => setKpiRules(ruleValue('#059669'), e.target.value)}
                        className="h-7 text-xs" placeholder="e.g. 50" />
                    </div>
                  </div>
                )}
              </div>

              <div className="p-2">
                <ChartRenderer
                  type={chart.type}
                  data={entry.data || []}
                  height={200}
                  valueLabel={valueLabel}
                  valueLabel2={chart.valueField2 ? `${chart.agg2 || 'SUM'} ${displayLabel(fieldMap[chart.valueField2]) || ''}` : 'Count'}
                  seriesKeys={entry.seriesKeys || null}
                  stacked={!!chart.stacked}
                  stack100={!!chart.stack100}
                  goalValue={entry.goalValue}
                  colorRules={chart.colorRules || null}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChartBuilder;
